
import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Facebook, Instagram, Phone, Users, X, Send, Share2, Download, Trash2, PlusCircle, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';

interface Contact {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

// Demo contacts for the page
const demoContacts: Contact[] = [
  { id: '1', name: 'Alex Johnson', email: 'alex@example.com' },
  { id: '2', name: 'Sam Smith', email: 'sam@example.com' },
  { id: '3', name: 'Taylor Brown', email: 'taylor@example.com' },
  { id: '4', name: 'Jordan Wilson', email: 'jordan@example.com' },
  { id: '5', name: 'Casey Miller', email: 'casey@example.com' },
  { id: '6', name: 'Morgan Lee', email: 'morgan@example.com' },
  { id: '7', name: 'Riley Thomas', email: 'riley@example.com' },
  { id: '8', name: 'Quinn Adams', email: 'quinn@example.com' },
  { id: '9', name: 'Jamie Garcia', email: 'jamie@example.com' },
  { id: '10', name: 'Drew Martin', email: 'drew@example.com' },
];

const socialPlatforms = [
  {
    id: 'facebook',
    name: 'Facebook',
    icon: <Facebook className="h-5 w-5" />,
    color: 'bg-blue-600',
    textColor: 'text-white',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: <Instagram className="h-5 w-5" />,
    color: 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500',
    textColor: 'text-white',
  },
  {
    id: 'twitter',
    name: 'Twitter',
    icon: <X className="h-5 w-5" />,
    color: 'bg-black',
    textColor: 'text-white',
  },
  {
    id: 'phone',
    name: 'Phone Contacts',
    icon: <Phone className="h-5 w-5" />,
    color: 'bg-green-600',
    textColor: 'text-white',
  },
];

const InviteFriends = () => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);
  const [contacts, setContacts] = useState<Contact[]>(demoContacts);
  const [searchTerm, setSearchTerm] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '' });
  
  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleDirectInvite = (type: 'email' | 'phone') => {
    const value = type === 'email' ? inviteEmail : invitePhone;
    
    if (!value) {
      toast({
        title: "Please enter a valid " + type,
        description: `Enter a ${type} to send an invitation.`,
        variant: "destructive"
      });
      return;
    }
    
    // In a real app, this would call an API to send the invitation
    toast({
      title: "Invitation sent!",
      description: `Your invitation has been sent to ${value}.`,
      variant: "default"
    });
    
    // Add email to the list if it's an email
    if (type === 'email') {
      setInviteEmails(prev => [...prev, inviteEmail]);
      setInviteEmail('');
    } else {
      setInvitePhone('');
    }
  };
  
  const handleSocialInvite = (platform: string) => {
    // In a real app, this would open the respective platform's sharing dialog
    toast({
      title: `${platform} invitation`,
      description: `This would connect to ${platform} to invite your contacts.`,
    });
  };
  
  const handleBulkInvite = () => {
    if (inviteEmails.length === 0) {
      toast({
        title: "No contacts to invite",
        description: "Add some email contacts first.",
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Bulk invitations sent!",
      description: `Successfully sent ${inviteEmails.length} invitations.`,
    });
  };
  
  const handleDownloadContacts = () => {
    // Create CSV content
    const headers = ['Name', 'Email'];
    const csvRows = [headers];
    
    // Add each contact as a row
    contacts.forEach(contact => {
      csvRows.push([contact.name, contact.email]);
    });
    
    // Convert to CSV string
    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    
    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'givgo_contacts.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Contacts downloaded",
      description: `${contacts.length} contacts saved to CSV file`,
    });
  };
  
  const handleDeleteContact = (id: string) => {
    setContacts(contacts.filter(contact => contact.id !== id));
    toast({
      title: "Contact deleted",
      description: "Contact successfully removed from your list",
    });
  };
  
  const handleAddContact = () => {
    if (!newContact.name || !newContact.email || !newContact.email.includes('@')) {
      toast({
        title: "Invalid contact information",
        description: "Please provide a valid name and email address",
        variant: "destructive"
      });
      return;
    }
    
    const newContactWithId: Contact = {
      id: `manual-${Date.now()}`,
      name: newContact.name,
      email: newContact.email
    };
    
    setContacts([...contacts, newContactWithId]);
    setNewContact({ name: '', email: '' });
    setShowContactForm(false);
    
    toast({
      title: "Contact added",
      description: `${newContact.name} was added to your contacts`,
    });
  };
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-block p-4 rounded-full bg-givgo-light mb-4">
            <Users className="h-8 w-8 text-givgo-purple" />
          </div>
          <h1 className="text-3xl font-bold text-givgo-purple">Invite Friends & Family</h1>
          <p className="text-muted-foreground mt-2">
            Invite your friends to join GivGo and start exchanging micro-gifts
          </p>
        </div>
        
        <Tabs defaultValue="invite" className="space-y-6">
          <TabsList className="grid grid-cols-2 w-full max-w-md mx-auto">
            <TabsTrigger value="invite">Invite Friends</TabsTrigger>
            <TabsTrigger value="contacts">Manage Contacts</TabsTrigger>
          </TabsList>
          
          <TabsContent value="invite">
            <Card>
              <CardHeader>
                <CardTitle>Invite Your Contacts</CardTitle>
                <CardDescription>Choose a method to invite your friends and family to GivGo</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="direct" className="mt-4">
                  <TabsList className="grid grid-cols-2 mb-4">
                    <TabsTrigger value="direct">Direct Invite</TabsTrigger>
                    <TabsTrigger value="social">Social Media</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="direct" className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-medium">Email</label>
                        <div className="flex gap-2">
                          <Input
                            id="email"
                            type="email"
                            placeholder="friend@example.com"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                          />
                          <Button 
                            variant="default" 
                            className="bg-givgo-purple hover:bg-givgo-purple/90"
                            onClick={() => handleDirectInvite('email')}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <label htmlFor="phone" className="text-sm font-medium">Phone Number</label>
                        <div className="flex gap-2">
                          <Input
                            id="phone"
                            type="tel"
                            placeholder="(123) 456-7890"
                            value={invitePhone}
                            onChange={(e) => setInvitePhone(e.target.value)}
                          />
                          <Button 
                            variant="default" 
                            className="bg-givgo-purple hover:bg-givgo-purple/90"
                            onClick={() => handleDirectInvite('phone')}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {inviteEmails.length > 0 && (
                      <div className="mt-8 space-y-4">
                        <div className="flex justify-between items-center">
                          <h3 className="font-medium">Pending Invitations ({inviteEmails.length})</h3>
                          <Button 
                            variant="default" 
                            size="sm"
                            className="bg-givgo-purple hover:bg-givgo-purple/90"
                            onClick={handleBulkInvite}
                          >
                            Send All
                          </Button>
                        </div>
                        <div className="border rounded-md p-4 max-h-48 overflow-y-auto">
                          {inviteEmails.map((email, index) => (
                            <div key={index} className="flex justify-between items-center py-2 border-b last:border-0">
                              <span>{email}</span>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-4 p-4 bg-givgo-light rounded-md">
                      <div className="flex gap-4 items-center">
                        <div className="rounded-full bg-givgo-purple text-white p-3">
                          <Share2 className="h-6 w-6" />
                        </div>
                        <div>
                          <h3 className="font-medium text-givgo-purple">Share Invitation Link</h3>
                          <p className="text-sm text-muted-foreground">
                            Copy your unique invitation link and share it with friends
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Input 
                          value="https://givgo.app/invite/user123" 
                          readOnly 
                          className="bg-white"
                        />
                        <Button 
                          variant="default" 
                          className="bg-givgo-purple hover:bg-givgo-purple/90"
                          onClick={() => {
                            // In a real app, this would copy to clipboard
                            toast({
                              title: "Link copied!",
                              description: "Invitation link copied to clipboard",
                            });
                          }}
                        >
                          Copy
                        </Button>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="social" className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {socialPlatforms.map((platform) => (
                      <Button
                        key={platform.id}
                        variant="outline"
                        className={`flex items-center justify-start gap-3 h-14 ${platform.color} ${platform.textColor} hover:opacity-90`}
                        onClick={() => handleSocialInvite(platform.name)}
                      >
                        {platform.icon}
                        <span>{platform.name}</span>
                      </Button>
                    ))}
                    
                    <Button
                      variant="outline"
                      className="flex items-center justify-start gap-3 h-14 bg-pink-500 text-white hover:opacity-90"
                      onClick={() => handleSocialInvite('TikTok')}
                    >
                      <span className="text-xl font-bold">TT</span>
                      <span>TikTok</span>
                    </Button>
                    
                    <Button
                      variant="outline"
                      className="flex items-center justify-start gap-3 h-14 bg-yellow-400 text-black hover:opacity-90"
                      onClick={() => handleSocialInvite('Snapchat')}
                    >
                      <span className="text-xl">👻</span>
                      <span>Snapchat</span>
                    </Button>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="contacts">
            <Card>
              <CardHeader>
                <CardTitle>Contact List</CardTitle>
                <CardDescription>Manage your GivGo contacts and download for review</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input 
                        placeholder="Search contacts..." 
                        className="pl-9"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={handleDownloadContacts}
                        className="flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        <span className="hidden sm:inline">Download CSV</span>
                      </Button>
                      <Button 
                        onClick={() => setShowContactForm(true)} 
                        className="bg-givgo-purple hover:bg-givgo-purple/90 flex items-center gap-2"
                      >
                        <PlusCircle className="h-4 w-4" />
                        <span className="hidden sm:inline">Add Contact</span>
                      </Button>
                    </div>
                  </div>
                  
                  {showContactForm && (
                    <div className="p-4 border rounded-md bg-slate-50 space-y-3">
                      <h3 className="font-medium">Add New Contact</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label htmlFor="contactName" className="text-sm font-medium block mb-1">Name</label>
                          <Input 
                            id="contactName" 
                            placeholder="Contact Name"
                            value={newContact.name}
                            onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                          />
                        </div>
                        <div>
                          <label htmlFor="contactEmail" className="text-sm font-medium block mb-1">Email</label>
                          <Input 
                            id="contactEmail" 
                            type="email"
                            placeholder="contact@example.com"
                            value={newContact.email}
                            onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 mt-2">
                        <Button variant="outline" onClick={() => setShowContactForm(false)}>Cancel</Button>
                        <Button 
                          className="bg-givgo-purple hover:bg-givgo-purple/90"
                          onClick={handleAddContact}
                        >
                          Add Contact
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div className="border rounded-md overflow-hidden">
                    <div className="grid grid-cols-3 gap-4 p-3 font-medium text-sm bg-slate-50 border-b">
                      <div>Name</div>
                      <div className="col-span-1">Email</div>
                      <div className="text-right">Actions</div>
                    </div>
                    
                    <div className="divide-y max-h-96 overflow-y-auto">
                      {filteredContacts.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                          {searchTerm ? 'No matching contacts found' : 'No contacts yet'}
                        </div>
                      ) : (
                        filteredContacts.map(contact => (
                          <div key={contact.id} className="grid grid-cols-3 gap-4 p-3 items-center">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-givgo-light flex items-center justify-center flex-shrink-0">
                                {contact.avatar ? (
                                  <img src={contact.avatar} alt={contact.name} className="h-8 w-8 rounded-full" />
                                ) : (
                                  <span className="text-givgo-purple font-medium">
                                    {contact.name.charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <span className="font-medium truncate">{contact.name}</span>
                            </div>
                            <div className="truncate text-muted-foreground">
                              {contact.email}
                            </div>
                            <div className="flex justify-end gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => {
                                  // In a real app, this would send an invitation
                                  toast({
                                    title: "Invitation sent",
                                    description: `Invitation sent to ${contact.name}`,
                                  });
                                }}
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteContact(contact.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    <div className="p-3 border-t bg-slate-50 flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        {filteredContacts.length} {filteredContacts.length === 1 ? 'contact' : 'contacts'} {searchTerm && 'found'}
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleDownloadContacts}
                        className="flex items-center gap-1"
                      >
                        <Download className="h-3 w-3" />
                        <span>Export</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <div className="mt-8 text-center p-6 bg-givgo-light rounded-lg">
          <h2 className="text-xl font-semibold text-givgo-purple mb-2">Why Invite Friends to GivGo?</h2>
          <p className="text-muted-foreground mb-4">
            Micro-gifting is more fun with friends! Invite your network and start spreading joy with GivGo.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white p-4 rounded-md shadow-sm">
              <div className="w-12 h-12 rounded-full bg-givgo-light mx-auto mb-3 flex items-center justify-center">
                <Users className="h-6 w-6 text-givgo-purple" />
              </div>
              <h3 className="font-medium">Build Your Network</h3>
              <p className="text-sm text-muted-foreground">Connect with friends and family for easy micro-gifting</p>
            </div>
            <div className="bg-white p-4 rounded-md shadow-sm">
              <div className="w-12 h-12 rounded-full bg-givgo-light mx-auto mb-3 flex items-center justify-center">
                <Send className="h-6 w-6 text-givgo-purple" />
              </div>
              <h3 className="font-medium">Send & Receive</h3>
              <p className="text-sm text-muted-foreground">Exchange micro-gifts with your closest contacts</p>
            </div>
            <div className="bg-white p-4 rounded-md shadow-sm">
              <div className="w-12 h-12 rounded-full bg-givgo-light mx-auto mb-3 flex items-center justify-center">
                <span className="text-givgo-purple text-lg">🎉</span>
              </div>
              <h3 className="font-medium">Spread Joy</h3>
              <p className="text-sm text-muted-foreground">Make someone's day with a thoughtful micro-gift</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default InviteFriends;
